import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import axios from 'axios';
import { useI18n } from '../i18n/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditModeContext = createContext({
  editMode: false,
  setEditMode: () => {},
  saveTranslation: async () => {},
  refresh: () => {},
});

export const EditModeProvider = ({ children }) => {
  const [editMode, setEditMode] = useState(false);
  // Pull-in a ref to the I18n context's setter via a global event — see below.
  // We avoid coupling to I18nContext internals: after each save we emit a
  // `translations-updated` window event; I18nProvider listens and re-fetches.

  const saveTranslation = useCallback(async (key, lang, value) => {
    const { data } = await axios.put(`${API}/admin/translations/single`,
      { key, lang, value }, { withCredentials: true });
    window.dispatchEvent(new Event('translations-updated'));
    return data;
  }, []);

  const value = useMemo(() => ({ editMode, setEditMode, saveTranslation }), [editMode, saveTranslation]);
  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
};

export const useEditMode = () => useContext(EditModeContext);

/** Editable text wrapper. Behaves as plain text for everyone except admin-with-edit-mode-on.
 *  Renders the translated text via t(keyName). When edit mode is on, becomes contentEditable
 *  with a dotted outline; saves on blur (if changed). */
export const Tx = ({ k, as = 'span', className = '', children, ...rest }) => {
  const { t, lang } = useI18n();
  const { editMode, saveTranslation } = useEditMode();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const Tag = as;
  const text = k ? t(k) : (typeof children === 'string' ? children : '');

  if (!editMode || !k) {
    // Normal render — keep the existing semantics. If children is a string, prefer it.
    return <Tag className={className} {...rest}>{k ? text : children}</Tag>;
  }

  const onBlur = async (e) => {
    const newValue = e.currentTarget.innerText.trim();
    if (newValue === text || !newValue) return;
    setSaving(true);
    setError('');
    try {
      await saveTranslation(k, lang, newValue);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message || 'Save failed';
      // 401 / 403 → admin session expired. Show a loud, persistent banner so the
      // user knows the edit was NOT saved (silent failure was the previous bug).
      if (status === 401 || status === 403) {
        setError('Session expired — please log in again.');
        try {
          window.dispatchEvent(new CustomEvent('inline-edit-auth-expired', { detail: { key: k } }));
        } catch (_) { /* noop */ }
      } else {
        setError(detail);
        try {
          window.dispatchEvent(new CustomEvent('inline-edit-save-error', { detail: { key: k, message: detail } }));
        } catch (_) { /* noop */ }
      }
      // Revert visually so the on-screen text matches the server's actual state.
      e.currentTarget.innerText = text;
    } finally {
      setSaving(false);
    }
  };

  const stopEvent = (e) => {
    // While editing, swallow the click/mousedown so clicks on text inside an
    // <a> or <button> don't navigate / submit. We still allow the default
    // behavior on the span itself so contentEditable can place the caret.
    e.stopPropagation();
    if (e.type === 'click') {
      // Anchor navigation triggers on the click event — block it here.
      // The span's own contentEditable still handles caret placement
      // because we don't call preventDefault on mousedown.
      e.preventDefault();
    }
  };

  return (
    <Tag
      data-editable-key={k}
      data-editable-lang={lang}
      contentEditable
      suppressContentEditableWarning
      onBlur={onBlur}
      onClick={stopEvent}
      onMouseDown={stopEvent}
      onKeyDown={(e) => {
        // Enter key commits (blurs) — Shift+Enter inserts newline.
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.currentTarget.innerText = text;
          e.currentTarget.blur();
        }
      }}
      title={`Editing: ${k} (${lang})${saving ? ' — saving…' : ''}${error ? ' — ' + error : ''}`}
      className={`outline outline-2 outline-dashed outline-amber-400 outline-offset-2 rounded px-1 -mx-1 bg-amber-50/40 hover:bg-amber-100/60 focus:outline-amber-500 focus:bg-amber-100 focus:shadow-lg cursor-text transition-all ${saving ? 'opacity-50' : ''} ${className}`}
      {...rest}
    >
      {text}
    </Tag>
  );
};

export default EditModeProvider;
